import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../../services/ApiService';
import type { BiltEvent } from '../../types/event';
import PageLoader from '../ui/PageLoader';
import Footer from '../Footer';
import EmptyState from '../ui/EmptyState';
import Button from '../ui/Button';
import { PageHeader } from '../ui/PageHeader';
import PageBackLink from '../ui/PageBackLink';
import { useOnboarding } from '../../hooks/useOnboarding';
import { JudgeGuideCard } from '../onboarding/OnboardingCards';
import { shouldShowJudgeGuide } from '../../utils/onboarding';
import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline';

const JudgeDashboard: React.FC = () => {
  const onboarding = useOnboarding();
  const [events, setEvents] = useState<BiltEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      setEvents(await apiService.getJudgeEvents());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load assigned events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadEvents();
  }, []);

  if (loading) {
    return (
      <>
        <PageLoader message="Loading assigned events…" />
        <Footer />
      </>
    );
  }

  const showGuide = shouldShowJudgeGuide(onboarding.state);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <div className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
        <PageBackLink label="Back to Events" to="/events" className="mb-4" />
        <PageHeader
          eyebrow="Judge"
          title="Judging"
          description="Score vehicles at your assigned events. Drafts and class filters are on each event list."
        />

        {showGuide && (
          <JudgeGuideCard onDismiss={() => void onboarding.dismissJudgeGuide()} />
        )}

        {error ? (
          <EmptyState
            icon={<ClipboardDocumentListIcon className="w-12 h-12" />}
            title="Couldn&apos;t load events"
            description={error}
            action={
              <Button type="button" variant="primary" className="min-h-[44px]" onClick={() => void loadEvents()}>
                Try again
              </Button>
            }
          />
        ) : events.length === 0 ? (
          <EmptyState
            icon={<ClipboardDocumentListIcon className="w-12 h-12" />}
            title="No assigned events yet"
            description="Once an organizer adds you as a judge, your events will appear here."
          />
        ) : (
          <ul className="space-y-3">
            {events.map((e) => (
              <li key={e.id}>
                <Link
                  to={`/judge/events/${e.id}`}
                  className="card-interactive block p-4"
                >
                  <p className="heading-card">{e.title}</p>
                  <p className="text-meta mt-1.5">
                    {e.city && e.state ? `${e.city}, ${e.state}` : 'Location TBD'}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default JudgeDashboard;
